"use client"

import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { useMessageNotifications } from "@/hooks/use-message-notifications"
import { useTranslation } from "@/hooks/use-translation"

/**
 * The message-notifications switch.
 *
 * Explicit Cancel / Save rather than a live toggle, because the consequence is not obvious from the
 * control: turning this off stops anything from telling you a message arrived, including a client
 * escalation. The warning is shown while the switch is off so the person reads it *before*
 * committing, not after.
 *
 * The copy is deliberately specific about what does NOT change — threads are not muted for anyone
 * else, nothing is deleted, and reading a thread while notifications are off does not mark it read.
 * That last one is the invariant that makes the switch safe, and saying so is what stops it feeling
 * like a destructive setting.
 */
export function MessageNotificationsModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { messageNotificationsEnabled, setMessageNotificationsEnabled, isSaving } = useMessageNotifications()
  const [draft, setDraft] = useState(messageNotificationsEnabled)

  // Reset the draft each time it opens, so a cancelled change never leaks into the next visit.
  useEffect(() => {
    if (open) setDraft(messageNotificationsEnabled)
  }, [open, messageNotificationsEnabled])

  const handleSave = async () => {
    const saved = await setMessageNotificationsEnabled(draft)
    if (saved) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t('messageNotifications') || 'Message notifications'}</DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-[#E0E3EB] p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-semibold text-[#0F214B]">{t('messageNotificationsShowLabel') || 'Show message notifications'}</div>
              <p className="mt-1 text-sm text-gray-600">
                {t('messageNotificationsShowDescription') || 'Unread counts on rows and tabs and important flags.'}
              </p>
            </div>
            {/* Navy when on, matching the app's active colour rather than the shadcn default. */}
            <Switch
              checked={draft}
              onCheckedChange={setDraft}
              aria-label={t('messageNotificationsShowLabel') || 'Show message notifications'}
              className="data-[state=checked]:bg-[#0F214B]"
            />
          </div>
        </div>

        {!draft && (
          <div className="rounded-lg border border-[#F3C2C2] bg-[#FDF2F2] p-4">
            <p className="font-semibold text-[#B42318]">{t('messageNotificationsWarningTitle') || 'Nothing will tell you a message has arrived.'}</p>
            <p className="mt-2 text-sm text-[#7A2E2E]">
              {t('messageNotificationsWarningBody') || 'No unread counts and no important flags — including on a message a client has escalated to you. Hover previews still work. Conversations are not deleted or muted for anyone else: threads stay where they are and you can open any of them at any time, you just have to go looking. Reading a thread while notifications are off does not mark it read, so nothing is lost while you are away from it. This changes your view only, on every tab and every client.'}
            </p>
          </div>
        )}

        {/* elliptical / elliptical-outline are the app's rounded pair — same as edit-columns. */}
        <DialogFooter>
          <div className="flex gap-2 w-full sm:w-auto sm:ml-auto">
            <Button
              variant="elliptical-outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
              className="flex-1 sm:flex-none"
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              variant="elliptical"
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 sm:flex-none"
            >
              {isSaving ? (t('processing') || 'Saving…') : (t('save') || 'Save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
